import Cross from "@icons/cross.svg?react";
import PlusSvg from "@icons/plus.svg?react";
import { Trans } from "@lingui/react/macro";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/common/components/button/button";
import { Dialog, useDialogProvider } from "@/common/components/dialog/dialog";
import { PageTitle } from "@/common/components/page-title/page-title";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { useLanguage } from "@/common/hooks/use-language";
import { invariant } from "@/common/utils/invariant";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { handleFormError } from "../shared/api/error-handler";
import { storageQuery } from "../shared/api/get-storage";
import { useAddStorage, useArchiveStorage, useEditStorage } from "./api";
import { CommodityCard } from "./components/commodity-card/commodity-card";
import { Fields, StorageForm } from "./components/storage-form/storage-form";

const path = "new";

const ExistingStorage = () => {
  const queryClient = useQueryClient();
  const { id } = useParams();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(id, "Storage id should be defined");
  const locale = useLanguage();
  const storage = useSuspenseQuery(storageQuery({ id, locale }));
  const editStorage = useEditStorage();
  const form = useForm<Fields>({
    defaultValues: {
      address: storage.data.address,
      companyId: storage.data.company.id,
      name: storage.data.name,
      postalCode: storage.data.postalCode,
    },
  });
  const [, currency] = useCurrency();
  const navigate = useNavigate();
  const archiveStorage = useArchiveStorage();
  const { closeDialog: closeDeleteDialog, openDialog: openDeleteDialog } =
    useDialogProvider();

  const handleSubmitStorage = (data: Fields) => {
    editStorage.mutate(
      {
        id,
        storage: {
          address: data.address,
          companyId: data.companyId,
          name: data.name,
          zip: data.postalCode,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
        },
      },
    );
  };

  const handleDeleteStorage = () => {
    archiveStorage.mutate(
      { id },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries();
          closeDeleteDialog();
        },
        onSuccess: async () => {
          await navigate("..");
        },
      },
    );
  };

  const DeleteDialog = (
    <Dialog>
      <Dialog.Title>
        <Trans>Opravdu chcete smazat sklad {storage.data.name}?</Trans>
      </Dialog.Title>
      <Dialog.Actions>
        <Dialog.Confirm onClick={handleDeleteStorage} type="button">
          <Trans>Ano</Trans>
        </Dialog.Confirm>
        <Dialog.Cancel onClick={closeDeleteDialog} type="button">
          <Trans>Ne</Trans>
        </Dialog.Cancel>
      </Dialog.Actions>
    </Dialog>
  );

  return (
    <FullscreenModalLayout
      headerButton={
        <SearchParamsLink replace to="..">
          <Cross />
        </SearchParamsLink>
      }
    >
      <Spacer className="h-6" />
      <PageTitle>{storage.data.name}</PageTitle>
      <Spacer className="h-6" />
      <StorageForm
        buttons={
          <>
            <Button.Delete
              disabled={archiveStorage.status === "pending"}
              onClick={() => openDeleteDialog(DeleteDialog)}
              type="button"
            >
              <Trans>Smazat sklad</Trans>
            </Button.Delete>
            <Button
              disabled={editStorage.status === "pending"}
              form="storage-form"
              type="submit"
              variant="primary"
            >
              <Trans>Uložit sklad</Trans>
            </Button>
          </>
        }
        form={form}
        onSubmit={handleSubmitStorage}
      >
        <h2 className="font-bold text-can-forest-teal">
          <Trans>Komodity skladem</Trans>
        </h2>
        {storage.data.offers.map((offer) => (
          <SearchParamsLink key={offer.id} to={`offers/${offer.id}`}>
            <CommodityCard>
              <CommodityCard.Title
                crop={{
                  harvestYear: offer.harvestYear,
                  mark: false,
                  name: offer.crop.name,
                }}
              />
              <CommodityCard.Price currency={currency} tons={offer.amount} />
              <CommodityCard.Stored tons={offer.amount} />
              <CommodityCard.ProgressBar
                values={{
                  freeAmount: offer.amount,
                  orderAmount: offer.orderAmount,
                  totalAmount: offer.totalAmount,
                }}
              />
              <CommodityCard.Available
                available={offer.totalAmount}
                order={offer.orderAmount}
                tons={offer.amount}
              />
            </CommodityCard>
          </SearchParamsLink>
        ))}
        <SearchParamsLink
          className="flex items-center justify-center gap-2"
          to="crops/new"
        >
          <PlusSvg />
          <Trans>Přidat komoditu</Trans>
        </SearchParamsLink>
      </StorageForm>
    </FullscreenModalLayout>
  );
};

const NewStorage = () => {
  const queryClient = useQueryClient();
  const company = useRequiredErpCompany();
  const initialValues: Fields = {
    address: "",
    companyId: company.id,
    name: "",
    postalCode: "",
  };
  const addStorage = useAddStorage();
  const form = useForm<Fields>({
    defaultValues: initialValues,
  });
  const navigate = useNavigate();

  const handleSubmitStorage = (data: Fields) => {
    addStorage.mutate(
      {
        address: data.address,
        companyId: data.companyId,
        name: data.name,
        zip: data.postalCode,
      },
      {
        onError: (e: Error) => {
          const errors = handleFormError(e);

          errors.forEach((error) => {
            if (error.field.includes("label")) {
              form.setError("name", { message: error.error.join("\n") });
            }

            if (error.field.includes("address")) {
              form.setError("address", { message: error.error.join("\n") });
            }
          });
        },
        onSuccess: async (data) => {
          await queryClient.invalidateQueries();
          await navigate(`../${data.addStorage?.id}`);
        },
      },
    );
  };

  return (
    <FullscreenModalLayout
      headerButton={
        <SearchParamsLink replace to="..">
          <Cross />
        </SearchParamsLink>
      }
    >
      <Spacer className="h-6" />
      <PageTitle>
        <Trans>Přidat sklad</Trans>
      </PageTitle>
      <Spacer className="h-6" />
      <StorageForm
        buttons={
          <Button
            disabled={addStorage.status === "pending"}
            form="storage-form"
            type="submit"
            variant="primary"
          >
            <Trans>Uložit a přidat sklad</Trans>
          </Button>
        }
        form={form}
        onSubmit={handleSubmitStorage}
      ></StorageForm>
    </FullscreenModalLayout>
  );
};

export { ExistingStorage, NewStorage, path };
